import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";

/**
 * Spring-style paging often arrives as `{ content: [...], page: { size, number, totalElements, totalPages } }`
 * or legacy flat `{ content, totalElements, totalPages, … }`.
 */
function extractPagedListPayload(response) {
    if (!response || typeof response !== "object") {
        return { list: [], pageMeta: null, pageBlock: response };
    }
    const inner = response.data != null && typeof response.data === "object" && !Array.isArray(response.data)
        ? response.data
        : null;
    const pageBlock =
        inner != null &&
        (Array.isArray(inner.content) ||
            inner.totalElements != null ||
            inner.total_elements != null ||
            inner.totalPages != null ||
            inner.total_pages != null ||
            inner.page != null)
            ? inner
            : response;
    const content =
        pageBlock?.content ?? response?.content ?? inner?.content ?? [];
    const list = Array.isArray(content) ? content : [];
    const pageMeta =
        response.page ??
        response?.data?.page ??
        pageBlock?.page ??
        inner?.page ??
        null;
    return { list, pageMeta, pageBlock, inner };
}

const HOME_SLIDING_BASE = "/home/sliding";

function extractDistinctTagsList(response) {
    if (response == null) return [];
    if (Array.isArray(response)) return response;
    if (typeof response !== "object") return [];
    if (response.code === "FAILURE") return null;

    const direct = [
        response.data,
        response.content,
        response.items,
        response.tags,
        response.result,
        response.payload,
    ];
    for (const val of direct) {
        if (Array.isArray(val)) return val;
    }
    if (response.data && typeof response.data === "object") {
        for (const val of [response.data.content, response.data.items, response.data.tags]) {
            if (Array.isArray(val)) return val;
        }
    }
    return [];
}

function normalizeDistinctTagRows(raw) {
    const list = extractDistinctTagsList(raw);
    if (list === null) return null;
    const seen = new Set();
    const out = [];
    for (const item of list) {
        let tag = "";
        let count;
        if (typeof item === "string") {
            tag = item.trim();
        } else if (item && typeof item === "object") {
            tag = String(item.tag ?? item.name ?? item.label ?? item.value ?? "").trim();
            const n = item.count;
            count = typeof n === "number" && !Number.isNaN(n) ? n : undefined;
        }
        if (!tag || tag.toLowerCase() === "bedrock") continue;
        const key = tag.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ tag, count });
    }
    return out;
}

const AdminSlidingService = {
    /** GET /home/sliding/distinct-tags — `{ tag, count }[]` */
    getDistinctTags: async (state, language) => {
        try {
            const params = {};
            const stateTrim = typeof state === "string" ? state.trim() : "";
            if (stateTrim) params.state = stateTrim;
            const langTrim = typeof language === "string" ? language.trim() : "";
            if (langTrim) params.language = langTrim;
            const response = await ApiService.getData(`${HOME_SLIDING_BASE}/distinct-tags`, params);
            const normalized = normalizeDistinctTagRows(response);
            if (normalized === null) {
                return {
                    success: false,
                    error: response?.message || "Failed to load distinct tags",
                    data: [],
                };
            }
            normalized.sort((a, b) => {
                const ca = typeof a.count === "number" ? a.count : 0;
                const cb = typeof b.count === "number" ? b.count : 0;
                if (cb !== ca) return cb - ca;
                return a.tag.localeCompare(b.tag, undefined, { sensitivity: "base" });
            });
            return { success: true, data: normalized };
        } catch (e) {
            Logger.error("AdminSlidingService.getDistinctTags:", e);
            return { success: false, error: e.message || "Failed to load distinct tags", data: [] };
        }
    },

    /**
     * Paginated sliding story groups — GET /v1/puzzle/sliding
     * Spring Page where each `content[]` item is SlidingStoryGroupRo:
     * `{ story: { id, imageUrl, language, … }, templates: SlidingRo[] }`
     * (templates ordered EASY → MEDIUM → HARD). Templates may embed a nested `story` summary.
     * @param {number} page — 0-based
     * @param {number} pageSize
     * @param {string} sort — e.g. createdAt:desc
     * @param {string} [state]
     * @param {string} [keyword]
     * @param {string} [language]
     * @param {Array} [tags]
     * @param {boolean} [free] — true = free only, false = paid only, omit = all
     * @param {boolean} [standaloneImage] — true = standalone only, false = not standalone, omit = all
     */
    list: async (page = 0, pageSize = 20, sort = "createdAt:desc", state, keyword, language, tags, free, standaloneImage, excludePack) => {
        try {
            const params = { page, pageSize, sort };
            if (state) params.state = state;
            if (free === true || free === false) params.free = free ? "true" : "false";
            if (standaloneImage === true || standaloneImage === false) {
                params.standaloneImage = standaloneImage ? "true" : "false";
            }
            const packCode = typeof excludePack === "string" ? excludePack.trim() : "";
            if (packCode) params.excludePack = packCode;
            const kw = typeof keyword === "string" ? keyword.trim() : "";
            if (kw) params.keyword = kw;
            const lang = typeof language === "string" ? language.trim() : "";
            if (lang) params.language = lang;
            if (tags && Array.isArray(tags) && tags.length > 0) {
                const tagStrings = tags
                    .map((tag) => {
                        const s = typeof tag === "string" ? tag : tag?.tag ?? String(tag ?? "");
                        return s.trim().toLowerCase();
                    })
                    .filter((t) => t.length > 0);
                if (tagStrings.length > 0) params.tags = tagStrings.join(",");
            }
            const response = await ApiService.getData("/v1/puzzle/sliding", params);
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to fetch sliding puzzles",
                };
            }
            const { list, pageMeta, pageBlock } = extractPagedListPayload(response);
            let content = Array.isArray(list) ? list : [];
            const toInt = (v) => {
                if (v == null) return null;
                const n = typeof v === "string" ? parseInt(v, 10) : Number(v);
                return Number.isFinite(n) ? n : null;
            };
            const totalElementsRaw =
                pageMeta?.totalElements ??
                pageMeta?.total_elements ??
                pageBlock?.totalElements ??
                pageBlock?.total_elements ??
                response?.totalElements ??
                response?.total_elements ??
                response?.data?.totalElements;
            const totalPagesRaw =
                pageMeta?.totalPages ??
                pageMeta?.total_pages ??
                pageBlock?.totalPages ??
                pageBlock?.total_pages ??
                response?.totalPages ??
                response?.data?.totalPages;
            const te = toInt(totalElementsRaw);
            const tp = toInt(totalPagesRaw);
            const totalElements = te != null ? te : content.length;
            const totalPages = tp != null && tp >= 0 ? tp : undefined;
            const numberRaw =
                pageMeta?.number ?? pageBlock?.number ?? response?.number ?? response?.data?.number;
            const sizeRaw =
                pageMeta?.size ?? pageBlock?.size ?? response?.size ?? response?.data?.size;
            return {
                success: true,
                data: {
                    content,
                    totalElements,
                    totalPages,
                    number: toInt(numberRaw),
                    size: toInt(sizeRaw),
                    raw: response,
                },
            };
        } catch (error) {
            Logger.error("Error fetching sliding puzzles:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch sliding puzzles",
            };
        }
    },

    /**
     * Single story group — GET /v1/puzzle/sliding/{storyId}/story
     * Success: SlidingStoryGroupRo:
     *   { story, storySliding: { id, free, publishedAt }, templates: SlidingRo[] }
     * Templates have per-row: id, gridSize, difficultyLevel, state, startingLayout, createdAt.
     * free + publishedAt live on storySliding (bundle row), not on individual templates.
     * Unknown story: CustomResponseRo with code NO_DATA.
     * Known story, no rows: 200 with templates: [].
     */
    getGroupByStoryId: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required", code: null, data: null };
            }
            const response = await ApiService.getData(
                `/v1/puzzle/sliding/${encodeURIComponent(storyId)}/story`
            );
            if (response && response.code === "NO_DATA") {
                return {
                    success: false,
                    code: "NO_DATA",
                    error: response.message || "Story not found",
                    data: null,
                };
            }
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    code: "FAILURE",
                    error: response.message || "Request failed",
                    data: null,
                };
            }
            if (
                response &&
                typeof response === "object" &&
                !Array.isArray(response) &&
                (typeof response.storyId === "string" ||
                    typeof response.story?.id === "string" ||
                    Array.isArray(response.templates))
            ) {
                const resolvedStoryId =
                    response.storyId ?? response.story?.id ?? storyId;
                return {
                    success: true,
                    code: null,
                    data: {
                        storyId: resolvedStoryId,
                        story: response.story ?? null,
                        storySliding: response.storySliding ?? null,
                        templates: Array.isArray(response.templates) ? response.templates : [],
                    },
                };
            }
            return {
                success: false,
                code: null,
                error: "Unexpected response from server",
                data: null,
            };
        } catch (error) {
            Logger.error("Error fetching sliding group by story:", error);
            return {
                success: false,
                code: null,
                error: error.message || "Failed to fetch sliding puzzles",
                data: null,
            };
        }
    },

    /**
     * Set free flag on the sliding story bundle.
     * PUT /v1/puzzle/sliding/{storyId}/story/free with body { free: true|false }.
     * @param {string} storyId
     * @param {boolean} free
     */
    setStoryTemplatesFree: async (storyId, free) => {
        try {
            if (!storyId || typeof storyId !== "string") {
                return { success: false, error: "Story id required", data: null };
            }
            const flag = free === true;
            const url = `/v1/puzzle/sliding/${encodeURIComponent(storyId)}/story/free?free=${flag ? "true" : "false"}`;
            const response = await ApiService.putData(url, { free: flag });
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to update story free flag",
                    data: null,
                };
            }
            const saved = response?.storySliding?.free ?? response?.data?.storySliding?.free;
            if (typeof saved === "boolean" && saved !== flag) {
                return {
                    success: false,
                    error: "Free flag did not save",
                    data: response ?? null,
                };
            }
            return { success: true, data: response ?? null, free: typeof saved === "boolean" ? saved : flag };
        } catch (error) {
            Logger.error("Error setting sliding story free:", error);
            return {
                success: false,
                error: error.message || "Failed to update story free flag",
                data: null,
            };
        }
    },

    /** PUT /v1/puzzle/sliding/{storyId}/story/editors-picks?editorsPicks=true */
    setStoryEditorsPicks: async (storyId, editorsPicks = true) => {
        try {
            if (!storyId) return { success: false, error: "Story id required" };
            const response = await ApiService.putData(
                `/v1/puzzle/sliding/${encodeURIComponent(storyId)}/story/editors-picks?editorsPicks=${editorsPicks}`,
                {},
            );
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminSlidingService.setStoryEditorsPicks:", e);
            return { success: false, error: e.message };
        }
    },

    /** PUT /v1/puzzle/sliding/{storyId}/story/tags — body `{ tags: string[] }` */
    setStoryTags: async (storyId, tags) => {
        try {
            if (!storyId) return { success: false, error: "Story id required" };
            const tagList = Array.isArray(tags)
                ? tags.map((tag) => {
                    const s = typeof tag === "string" ? tag : tag?.tag ?? String(tag ?? "");
                    return s.trim();
                }).filter((t) => t.length > 0)
                : [];
            const response = await ApiService.putData(
                `/v1/puzzle/sliding/${encodeURIComponent(storyId)}/story/tags`,
                { tags: tagList },
            );
            if (response && response.code === "FAILURE") {
                return { success: false, error: response.message || "Failed to update tags" };
            }
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminSlidingService.setStoryTags:", e);
            return { success: false, error: e.message || "Failed to update tags" };
        }
    },

    /**
     * Single sliding template — GET /v1/puzzle/sliding/{id}
     * Returns SlidingRo (includes nested story, per-row state / gridSize / difficultyLevel /
     * startingLayout, and free + publishedAt from the story's single story_sliding row).
     * @param {string} id — template UUID
     */
    getById: async (id) => {
        try {
            if (!id || typeof id !== "string" || !id.trim()) {
                return { success: false, error: "Template id required", data: null };
            }
            const response = await ApiService.getData(
                `/v1/puzzle/sliding/${encodeURIComponent(id.trim())}`
            );
            if (response && response.code === "NO_DATA") {
                return {
                    success: false,
                    code: "NO_DATA",
                    error: response.message || "Template not found",
                    data: null,
                };
            }
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    code: "FAILURE",
                    error: response.message || "Request failed",
                    data: null,
                };
            }
            return { success: true, code: null, data: response ?? null };
        } catch (error) {
            Logger.error("AdminSlidingService.getById:", error);
            return {
                success: false,
                code: null,
                error: error.message || "Failed to fetch sliding template",
                data: null,
            };
        }
    },

    /**
     * Update a sliding template — PUT /v1/puzzle/sliding?makeWeakAgo=true|false
     *
     * Body fields (all optional except `id`):
     *   id, storyId, gridSize, difficultyLevel, state, free, startingLayout
     *
     * When `makeWeakAgo=true` the backend forces state → PUBLISHED and sets publishedAt to a
     * random weekday 09:00–17:00 (America/Montreal) in the next 7 days; the body `state` is ignored.
     *
     * Response: SlidingRo (same shape as GET /v1/puzzle/sliding/{id}).
     *
     * @param {object} fields
     * @param {string}   fields.id               — template UUID (required)
     * @param {string}   [fields.storyId]         — move to another story
     * @param {number}   [fields.gridSize]
     * @param {string}   [fields.difficultyLevel] — EASY | MEDIUM | HARD
     * @param {string}   [fields.state]           — e.g. REVIEWED, PUBLISHED (ignored when makeWeakAgo=true)
     * @param {boolean}  [fields.free]
     * @param {number[]} [fields.startingLayout]
     * @param {boolean}  [makeWeakAgo=false]
     */
    updateTemplate: async (fields = {}, makeWeakAgo = false) => {
        try {
            const { id, storyId, gridSize, difficultyLevel, state, free, startingLayout } = fields;
            if (!id || typeof id !== "string" || !id.trim()) {
                return { success: false, error: "Template id required", data: null };
            }
            const body = { id: id.trim() };
            if (storyId != null && typeof storyId === "string" && storyId.trim()) {
                body.storyId = storyId.trim();
            }
            if (gridSize != null) {
                const gs = Number(gridSize);
                if (Number.isFinite(gs) && gs > 0) body.gridSize = gs;
            }
            if (difficultyLevel != null && typeof difficultyLevel === "string" && difficultyLevel.trim()) {
                body.difficultyLevel = difficultyLevel.trim();
            }
            if (state != null && typeof state === "string" && state.trim()) {
                body.state = state.trim();
            }
            if (free === true || free === false) {
                body.free = free;
            }
            if (Array.isArray(startingLayout) && startingLayout.length > 0) {
                body.startingLayout = startingLayout.map(Number);
            }
            const weak = makeWeakAgo === true ? "true" : "false";
            const response = await ApiService.putData(
                `/v1/puzzle/sliding?makeWeakAgo=${weak}`,
                body
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to update sliding template",
                    data: null,
                };
            }
            return { success: true, data: response ?? null };
        } catch (error) {
            Logger.error("AdminSlidingService.updateTemplate:", error);
            return {
                success: false,
                error: error.message || "Failed to update sliding template",
                data: null,
            };
        }
    },

    /**
     * Publish one story bundle now (REVIEWED → PUBLISHED, published_at = now).
     * POST /v1/puzzle/sliding/{storyId}/publish
     */
    publishStory: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required" };
            }
            const response = await ApiService.postData(
                `/v1/puzzle/sliding/${encodeURIComponent(storyId)}/publish`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Publish failed" };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("AdminSlidingService.publishStory:", error);
            return { success: false, error: error.message || "Failed to publish sliding story" };
        }
    },

    /**
     * PUBLISHED → DISABLED. App cannot play it; discarded after 1 week.
     * Also disables linked jigsaw and word puzzles for the same story.
     * PUT /v1/puzzle/sliding/{storyId}/story/disable
     */
    disableStory: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required" };
            }
            const response = await ApiService.putData(
                `/v1/puzzle/sliding/${encodeURIComponent(storyId)}/story/disable`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Disable failed" };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("AdminSlidingService.disableStory:", error);
            return { success: false, error: error.message || "Failed to disable sliding story" };
        }
    },

    /**
     * DRAFT → REVIEWED so the bundle is eligible for batch publish.
     * POST /v1/puzzle/sliding/{storyId}/mark-reviewed
     */
    markReviewed: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required" };
            }
            const response = await ApiService.postData(
                `/v1/puzzle/sliding/${encodeURIComponent(storyId)}/mark-reviewed`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Mark reviewed failed" };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("AdminSlidingService.markReviewed:", error);
            return { success: false, error: error.message || "Failed to mark sliding story REVIEWED" };
        }
    },

    /**
     * Batch-publish oldest REVIEWED sliding bundles.
     * POST /v1/puzzle/sliding/publish?batchSize=…&language=…&tag=…
     */
    publishReviewed: async (batchSize = 10, language, tag) => {
        try {
            const n = Number(batchSize);
            const clamped = Number.isFinite(n) ? Math.min(100, Math.max(1, Math.round(n))) : 10;
            const params = new URLSearchParams();
            params.set("batchSize", String(clamped));
            const lang = language != null ? String(language).trim() : "";
            if (lang) params.set("language", lang);
            const tagTrim = tag != null ? String(tag).trim() : "";
            if (tagTrim) params.set("tag", tagTrim);
            const response = await ApiService.postData(
                `/v1/puzzle/sliding/publish?${params.toString()}`,
                {}
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Publish failed",
                    data: null,
                };
            }
            return {
                success: true,
                data: {
                    requestedBatchSize:
                        response?.requestedBatchSize ?? response?.data?.requestedBatchSize ?? clamped,
                    publishedCount: response?.publishedCount ?? response?.data?.publishedCount ?? 0,
                    storyIds: response?.storyIds ?? response?.data?.storyIds ?? [],
                    raw: response,
                },
            };
        } catch (error) {
            Logger.error("AdminSlidingService.publishReviewed:", error);
            return {
                success: false,
                error: error.message || "Failed to publish sliding puzzles",
                data: null,
            };
        }
    },

    /**
     * POST /v1/puzzle/sliding/{storyId}/gen-preview
     * Response: list of SlidingPreviewGenItem { slidingId, previewUrl, s3Key, difficultyLevel, gridSize }
     * @returns {{ success: boolean, data?: { items: Array }, error?: string }}
     */
    genPreview: async (storyId) => {
        try {
            if (!storyId || typeof storyId !== "string" || !storyId.trim()) {
                return { success: false, error: "Story id required", data: null };
            }
            const response = await ApiService.postData(
                `/v1/puzzle/sliding/${encodeURIComponent(storyId.trim())}/gen-preview`,
                {}
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to generate preview",
                    data: null,
                };
            }
            const rawList = Array.isArray(response)
                ? response
                : Array.isArray(response?.items)
                  ? response.items
                  : Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response?.data?.items)
                      ? response.data.items
                      : response?.previewUrl || response?.slidingId
                        ? [response]
                        : [];
            const items = rawList
                .map((row) => {
                    if (!row || typeof row !== "object") return null;
                    const slidingId = row.slidingId ?? row.sliding_id ?? "";
                    const previewUrl = row.previewUrl ?? row.preview_url ?? "";
                    const s3Key = row.s3Key ?? row.s3_key ?? "";
                    const difficultyLevel = row.difficultyLevel ?? row.difficulty_level ?? "";
                    const gridSize = row.gridSize ?? row.grid_size ?? null;
                    return {
                        slidingId: typeof slidingId === "string" ? slidingId.trim() : String(slidingId ?? ""),
                        previewUrl: typeof previewUrl === "string" ? previewUrl.trim() : "",
                        s3Key: typeof s3Key === "string" ? s3Key.trim() : "",
                        difficultyLevel:
                            typeof difficultyLevel === "string"
                                ? difficultyLevel.trim()
                                : String(difficultyLevel ?? ""),
                        gridSize:
                            gridSize != null && Number.isFinite(Number(gridSize))
                                ? Number(gridSize)
                                : null,
                    };
                })
                .filter(Boolean);
            return {
                success: true,
                data: { items, raw: response },
            };
        } catch (error) {
            Logger.error("AdminSlidingService.genPreview:", error);
            return {
                success: false,
                error: error.message || "Failed to generate preview",
                data: null,
            };
        }
    },

    /**
     * POST /v1/puzzle/sliding/gen-pre-image/for-null?size=…
     * Batch-generate preview images for sliding templates missing assets.
     */
    genPreImageForNull: async (size = 10) => {
        try {
            const n = Number(size);
            const clamped = Number.isFinite(n) ? Math.min(100, Math.max(1, Math.round(n))) : 10;
            const response = await ApiService.postData(
                `/v1/puzzle/sliding/gen-pre-image/for-null?size=${encodeURIComponent(clamped)}`,
                {}
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to generate preview images",
                    data: null,
                };
            }
            return {
                success: true,
                data: {
                    totalFound: response?.totalFound ?? response?.data?.totalFound,
                    processed: response?.processed ?? response?.data?.processed,
                    titles: response?.titles ?? response?.data?.titles ?? [],
                    errors: response?.errors ?? response?.data?.errors ?? [],
                    raw: response,
                },
            };
        } catch (error) {
            Logger.error("AdminSlidingService.genPreImageForNull:", error);
            return {
                success: false,
                error: error.message || "Failed to generate preview images",
                data: null,
            };
        }
    },

    /**
     * POST /v1/puzzle/sliding/gen-pre-image/refresh?size=…&afterStoryId=… or run-all (?all=true, ?processAll=true, ?size=all)
     * Regenerate preview/share PNGs for sliding stories that already have assets (overwrites old-format images).
     */
    genPreImageRefresh: async (size = 10, { runAll = false, state, language, afterStoryId } = {}) => {
        try {
            const params = new URLSearchParams();
            if (runAll) {
                params.set("all", "true");
                params.set("processAll", "true");
                params.set("size", "all");
            } else {
                const n = Number(size);
                const clamped = Number.isFinite(n) ? Math.min(100, Math.max(1, Math.round(n))) : 10;
                params.set("size", String(clamped));
            }
            const stateTrim = state != null ? String(state).trim() : "";
            if (stateTrim) params.set("state", stateTrim);
            const langTrim = language != null ? String(language).trim() : "";
            if (langTrim) params.set("language", langTrim);
            const afterStoryIdTrim = afterStoryId != null ? String(afterStoryId).trim() : "";
            if (afterStoryIdTrim) params.set("afterStoryId", afterStoryIdTrim);
            const response = await ApiService.postData(
                `/v1/puzzle/sliding/gen-pre-image/refresh?${params.toString()}`,
                {}
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to refresh preview images",
                    data: null,
                };
            }
            const root = response?.data && typeof response.data === "object" ? response.data : response;
            return {
                success: true,
                data: {
                    candidates: root?.candidates ?? undefined,
                    processed: root?.processed ?? undefined,
                    results: root?.results ?? [],
                    errors: root?.errors ?? [],
                    all: root?.all ?? undefined,
                    runAll: root?.runAll ?? undefined,
                    sizeLimit: root?.sizeLimit !== undefined ? root.sizeLimit : undefined,
                    totalEligible: root?.totalEligible ?? undefined,
                    bundleStates: root?.bundleStates ?? undefined,
                    language: root?.language ?? undefined,
                    requestedRunAll: runAll,
                    raw: response,
                },
            };
        } catch (error) {
            Logger.error("AdminSlidingService.genPreImageRefresh:", error);
            return {
                success: false,
                error: error.message || "Failed to refresh preview images",
                data: null,
            };
        }
    },

    /**
     * POST /v1/puzzle/sliding/gen-pre-image/refresh/async — starts a background refresh and returns job status.
     * Rendering outlives the gateway timeout, so the sweep runs server-side and progress is polled instead.
     */
    genPreImageRefreshAsync: async (size = 10, { runAll = false, state, language, afterStoryId } = {}) => {
        try {
            const params = new URLSearchParams();
            if (runAll) {
                params.set("all", "true");
                params.set("processAll", "true");
                params.set("size", "all");
            } else {
                const n = Number(size);
                const clamped = Number.isFinite(n) ? Math.min(100, Math.max(1, Math.round(n))) : 10;
                params.set("size", String(clamped));
            }
            const stateTrim = state != null ? String(state).trim() : "";
            if (stateTrim) params.set("state", stateTrim);
            const langTrim = language != null ? String(language).trim() : "";
            if (langTrim) params.set("language", langTrim);
            const afterStoryIdTrim = afterStoryId != null ? String(afterStoryId).trim() : "";
            if (afterStoryIdTrim) params.set("afterStoryId", afterStoryIdTrim);
            const response = await ApiService.postData(
                `/v1/puzzle/sliding/gen-pre-image/refresh/async?${params.toString()}`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Failed to start refresh job", data: null };
            }
            const root = response?.data && typeof response.data === "object" ? response.data : response;
            return { success: true, data: root };
        } catch (error) {
            Logger.error("AdminSlidingService.genPreImageRefreshAsync:", error);
            return { success: false, error: error.message || "Failed to start refresh job", data: null };
        }
    },

    /** GET /v1/puzzle/sliding/gen-pre-image/refresh/job — omit jobId for the latest sliding job. */
    getPreImageRefreshJob: async (jobId) => {
        try {
            const trimmed = jobId != null ? String(jobId).trim() : "";
            const response = await ApiService.getData(
                "/v1/puzzle/sliding/gen-pre-image/refresh/job",
                trimmed ? { jobId: trimmed } : null
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "No refresh job found", data: null };
            }
            const root = response?.data && typeof response.data === "object" ? response.data : response;
            return { success: true, data: root };
        } catch (error) {
            Logger.error("AdminSlidingService.getPreImageRefreshJob:", error);
            return { success: false, error: error.message || "Failed to read refresh job", data: null };
        }
    },

    /** POST /v1/puzzle/sliding/gen-pre-image/refresh/job/{jobId}/cancel — stops after the story in flight. */
    cancelPreImageRefreshJob: async (jobId) => {
        try {
            const trimmed = jobId != null ? String(jobId).trim() : "";
            if (!trimmed) {
                return { success: false, error: "Job id required", data: null };
            }
            const response = await ApiService.postData(
                `/v1/puzzle/sliding/gen-pre-image/refresh/job/${encodeURIComponent(trimmed)}/cancel`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Failed to cancel refresh job", data: null };
            }
            const root = response?.data && typeof response.data === "object" ? response.data : response;
            return { success: true, data: root };
        } catch (error) {
            Logger.error("AdminSlidingService.cancelPreImageRefreshJob:", error);
            return { success: false, error: error.message || "Failed to cancel refresh job", data: null };
        }
    },

    /** DELETE /v1/puzzle/sliding/{storyId}/story — sliding templates, plays, and DISCARDED tombstone; story stays */
    removeByStoryId: async (storyId) => {
        try {
            if (!storyId) return { success: false, error: "Story id required" };
            const response = await ApiService.deleteData(
                `/v1/puzzle/sliding/${encodeURIComponent(storyId)}/story`
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Delete failed" };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("AdminSlidingService.removeByStoryId:", error);
            return { success: false, error: error.message || "Delete failed" };
        }
    },

    genSlidingNullStory: async (size = 10, makeWeakAgo = false) => {
        try {
            const n = Number(size);
            const clamped = Number.isFinite(n) ? Math.min(100, Math.max(1, Math.round(n))) : 10;
            const weak = makeWeakAgo === true ? "true" : "false";
            const response = await ApiService.postData(
                `/v1/puzzle/sliding/gen-sliding/null-story?size=${encodeURIComponent(clamped)}&makeWeakAgo=${weak}`,
                {}
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Generation failed",
                    data: null,
                };
            }
            return {
                success: true,
                data: {
                    candidates:
                        response?.candidates ?? response?.data?.candidates ??
                        undefined,
                    processed:
                        response?.processed ?? response?.data?.processed ?? undefined,
                    results:
                        response?.results ?? response?.data?.results ?? [],
                    errors: response?.errors ?? response?.data?.errors ?? [],
                    raw: response,
                },
            };
        } catch (error) {
            Logger.error("Error in genSlidingNullStory:", error);
            return {
                success: false,
                error: error.message || "Failed to generate sliding puzzles",
                data: null,
            };
        }
    },
};

export default AdminSlidingService;
